import React, { useState, useEffect } from 'react';
import { useWarehouses } from '../../hooks/useWarehouses';
import { usePOSProducts, POSProduct } from '../../hooks/usePOSProducts';
import { Warehouse } from '../../types/warehouse';
import { useCategories } from '../../hooks/useCategories';
import { useNotification } from '../../hooks/useNotification';
import NotificationModal from '../common/NotificationModal';
import LoadingScreen from '../common/LoadingScreen';
import {
  MagnifyingGlassIcon,
  ShoppingCartIcon,
  UserIcon,
  CreditCardIcon,
  PrinterIcon,
  PlusIcon,
  MinusIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon,
  BuildingStorefrontIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ClockIcon,
  CubeIcon,
  FunnelIcon,
  TagIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  BuildingOfficeIcon,
  Squares2X2Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BanknotesIcon,
  BuildingLibraryIcon
} from '@heroicons/react/24/outline';
import { useBillingSettings } from '../../hooks/useBillingSettings';
import { billingService } from '../../services/billingService';
import { InvoiceRequest, InvoiceItem } from '../../types/billing';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

interface CartItem {
  productId: string;
  productCode: string;
  productName: string;
  unitPrice: number;
  quantity: number;
  taxRate: number;
  lineTotal: number;
  availableInCurrentWarehouse: number;
  stockSources: StockSource[];
}

interface StockSource {
  warehouseId: string;
  warehouseName: string;
  availableQuantity: number;
}

interface Customer {
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
}

interface StockTransferRequest {
  productId: string;
  productName: string;
  fromWarehouseId: string;
  fromWarehouseName: string;
  toWarehouseId: string;
  toWarehouseName: string;
  quantity: number;
  status: 'pending' | 'approved' | 'in_transit' | 'completed' | 'rejected';
}

const SalesChannelList: React.FC = () => {
  const { t } = useLanguage();
  const { warehouses, loading: warehousesLoading } = useWarehouses();
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  
  // Notification system
  const {
    notification,
    closeNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo
  } = useNotification();
  
  // Szűrő állapotok
  const [filters, setFilters] = useState({
    searchTerm: '',
    categoryId: '',
    hasStock: undefined as boolean | undefined,
    hasAnyStock: undefined as boolean | undefined,
    isService: undefined as boolean | undefined,
    productType: '',
    isActive: undefined as boolean | undefined
  });
  const [showFilters, setShowFilters] = useState(false);
  
  // Paginációs állapotok
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  const { products, categories, getProductStock, pagination, loading: productsLoading } = usePOSProducts(
    filters, 
    selectedWarehouse?.id, 
    currentPage, 
    pageSize
  );
  
  const [cart, setCart] = useState<CartItem[]>([]);
  const [customer, setCustomer] = useState<Customer>({});
  const [paymentMethod, setPaymentMethod] = useState<'CASH' | 'CARD' | 'TRANSFER'>('CASH');
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<POSProduct | null>(null);
  const [stockTransferRequests, setStockTransferRequests] = useState<StockTransferRequest[]>([]);

  // Használjuk a meglévő kategória hook-ot
  const { mainCategories, loading: categoriesLoading } = useCategories();

  const { user } = useAuth();
  const { billingSettings } = useBillingSettings();

  // Auto-select first active warehouse only on initial load
  useEffect(() => {
    if (warehouses.length > 0 && !hasInitialized) {
      // Ne válasszon automatikusan raktárt, maradjon a "Mind" opció alapértelmezetten
      setHasInitialized(true);
    }
  }, [warehouses, hasInitialized]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Raktár ikon kiválasztása típus szerint
  const getWarehouseIcon = (salesChannelType?: string) => {
    switch (salesChannelType) {
      case 'STORE':
        return BuildingStorefrontIcon;
      case 'WEBSHOP':
        return ComputerDesktopIcon;
      case 'WHOLESALE':
        return BuildingOfficeIcon;
      case 'MIXED':
        return Squares2X2Icon;
      default:
        return BuildingStorefrontIcon;
    }
  };

  // Készlet információ lekérése termékhez (valódi Firebase adatokkal)
  const getStockInfo = (productId: string) => {
    const stockInfo = getProductStock(productId);
    
    // Ha "Bármelyik üzlet" van kiválasztva, akkor az összes készletet mutatjuk
    if (selectedWarehouse === null) {
      // Raktár névvel kiegészített stock sources (összes raktár)
      const stockSources: StockSource[] = stockInfo.stockSources.map(source => {
        const warehouse = warehouses.find(w => w.id === source.warehouseId);
        return {
          warehouseId: source.warehouseId,
          warehouseName: warehouse?.name || t('sales.warehouse.unknown'),
          availableQuantity: source.availableQuantity
        };
      });

      return {
        currentWarehouseStock: stockInfo.totalStock, // Összes készlet
        stockSources
      };
    }
    
    // Raktár névvel kiegészített stock sources (kiválasztott raktár)
    const stockSources: StockSource[] = stockInfo.stockSources.map(source => {
      const warehouse = warehouses.find(w => w.id === source.warehouseId);
      return {
        warehouseId: source.warehouseId,
        warehouseName: warehouse?.name || t('sales.warehouse.unknown'),
        availableQuantity: source.availableQuantity
      };
    });

    return {
      currentWarehouseStock: stockInfo.currentWarehouseStock,
      stockSources
    };
  };

  const addToCart = (product: POSProduct) => {
    const stockInfo = getStockInfo(product.id);
    
    // Ha "Bármelyik üzlet" mód van és nincs készlet
    if (selectedWarehouse === null) {
      if (stockInfo.currentWarehouseStock === 0) {
        showError(t('sales.messages.error.noStock'), t('sales.messages.error.noStockAnyWarehouse'));
        return;
      }
      
      // Ha van készlet, de először válasszon raktárt
      showWarning(t('sales.messages.warning.warehouseRequired'), t('sales.messages.warning.selectWarehouse'));
      return;
    }
    
    if (stockInfo.currentWarehouseStock === 0 && stockInfo.stockSources.length > 0) {
      // Ha nincs készlet az aktuális raktárban, de máshol van
      setSelectedProduct(product);
      setShowStockModal(true);
      return;
    }

    if (stockInfo.currentWarehouseStock === 0) {
      showError(t('sales.messages.error.noStock'), t('sales.messages.error.noStockAnyWarehouse'));
      return;
    }

    const existingItem = cart.find(item => item.productId === product.id);
    
    if (existingItem) {
      const newQuantity = existingItem.quantity + 1;
      if (newQuantity > stockInfo.currentWarehouseStock) {
        showWarning(t('sales.messages.warning.insufficientStock'), t('sales.messages.warning.onlyAvailable', { count: stockInfo.currentWarehouseStock }));
        return;
      }
      updateQuantity(product.id, newQuantity);
    } else {
      const newItem: CartItem = {
        productId: product.id,
        productCode: product.code,
        productName: product.name,
        unitPrice: product.netPrice || 0,
        quantity: 1,
        taxRate: (() => {
          const rate = parseFloat(product.taxRate?.replace('%', '') || '27');
          const finalRate = isNaN(rate) ? 27 : rate;
          console.log(`Termék ${product.name} - taxRate: "${product.taxRate}" -> parsed: ${rate} -> final: ${finalRate}`);
          return finalRate;
        })(),
        lineTotal: product.netPrice || 0,
        availableInCurrentWarehouse: stockInfo.currentWarehouseStock,
        stockSources: stockInfo.stockSources
      };
      setCart([...cart, newItem]);
    }
  };

  const updateQuantity = (productId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    const item = cart.find(item => item.productId === productId);
    if (item && newQuantity > item.availableInCurrentWarehouse) {
      showWarning(t('sales.messages.warning.insufficientStock'), t('sales.messages.warning.onlyAvailable', { count: item.availableInCurrentWarehouse }));
      return;
    }

    setCart(cart.map(item => 
      item.productId === productId 
        ? { ...item, quantity: newQuantity, lineTotal: item.unitPrice * newQuantity }
        : item
    ));
  };

  const removeFromCart = (productId: string) => {
    setCart(cart.filter(item => item.productId !== productId));
  };

  const requestStockTransfer = (fromWarehouseId: string, quantity: number) => {
    if (!selectedProduct || !selectedWarehouse) return;

    const fromWarehouse = warehouses.find(w => w.id === fromWarehouseId);
    
    const transferRequest: StockTransferRequest = {
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      fromWarehouseId,
      fromWarehouseName: fromWarehouse?.name || t('sales.warehouse.unknown'),
      toWarehouseId: selectedWarehouse.id,
      toWarehouseName: selectedWarehouse.name,
      quantity,
      status: 'pending'
    };

    setStockTransferRequests([...stockTransferRequests, transferRequest]);
    setShowStockModal(false);
    setSelectedProduct(null);
    
    showInfo(t('sales.messages.success.stockTransferSent'), `${quantity} ${t('sales.unit')} ${selectedProduct.name} ${t('sales.stock.transfer.success')} ${fromWarehouse?.name} raktárból.`);
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => {
      const lineTotal = isNaN(item.lineTotal) ? 0 : item.lineTotal;
      return sum + lineTotal;
    }, 0);
    
    const taxAmount = cart.reduce((sum, item) => {
      const lineTotal = isNaN(item.lineTotal) ? 0 : item.lineTotal;
      const taxRate = isNaN(item.taxRate) ? 27 : item.taxRate;
      return sum + (lineTotal * taxRate / 100);
    }, 0);
    
    const total = subtotal + taxAmount;
    
    return { 
      subtotal: isNaN(subtotal) ? 0 : subtotal, 
      taxAmount: isNaN(taxAmount) ? 0 : taxAmount, 
      total: isNaN(total) ? 0 : total 
    };
  };

  const handleSale = async () => {
    if (!selectedWarehouse || cart.length === 0) return;

    // Ellenőrizzük, hogy minden termék elérhető-e
    const insufficientStock = cart.find(item => item.quantity > item.availableInCurrentWarehouse);
    if (insufficientStock) {
      showWarning(t('sales.messages.warning.insufficientStock'), t('sales.messages.warning.missingStock', { product: insufficientStock.productName }));
      return;
    }

    const { subtotal, taxAmount, total } = calculateTotals();
    
    // Itt majd a Firebase-be mentjük az értékesítést és a készletmozgásokat
    console.log('Értékesítés adatok:', {
      warehouseId: selectedWarehouse.id,
      items: cart,
      customer,
      paymentMethod,
      subtotal,
      taxAmount,
      total
    });

    // Kosár ürítése és visszajelzés
    setCart([]);
    setCustomer({});
    setShowCustomerForm(false);
    showSuccess(t('sales.messages.success.saleCompleted'), t('sales.messages.success.saleRecorded'));

    // Invoice generation
    const invoiceRequest: InvoiceRequest = {
      buyer: {
        name: customer.name || t('sales.customer.default'),
        email: customer.email || '',
        phone: customer.phone || '',
        address: {
          street: customer.address || '',
          city: '',
          postalCode: '',
          country: t('sales.customer.country')
        }
      },
      items: cart.map(item => ({
        name: item.productName,
        quantity: item.quantity,
        unit: t('sales.unit'),
        unitPrice: item.unitPrice,
        taxRate: item.taxRate
      })),
      paymentMethod,
      paymentDeadline: 8, // Alapértelmezett fizetési határidő
      currency: t('sales.currency'),
      organizationId: user?.organizationId || ''
    };

    // Számla kiállítása, ha be van állítva az integráció
    if (billingSettings && billingSettings.isActive) {
      try {
        const invoiceResponse = await billingService.createInvoice(invoiceRequest, user?.organizationId || '');
        if (invoiceResponse.success) {
          showSuccess(t('sales.messages.success.invoiceGenerated'), `Számla száma: ${invoiceResponse.invoiceNumber}`);
        } else {
          showError(t('sales.messages.error.invoiceError'), invoiceResponse.error || t('sales.messages.error.unknownError'));
        }
      } catch (error) {
        console.error('Invoice error:', error);
        showError(t('sales.messages.error.invoiceError'), t('sales.messages.error.invoiceFailed'));
      }
    }
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      categoryId: '',
      hasStock: undefined,
      hasAnyStock: undefined,
      isService: undefined,
      productType: '',
      isActive: undefined
    });
    setCurrentPage(1);
  };

  // Pagination helpers
  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (pagination.hasPreviousPage) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (pagination.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getPageNumbers = () => {
    const { currentPage, totalPages } = pagination;
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    return pages;
  };

  const hasActiveFilters = filters.categoryId || filters.hasStock !== undefined || 
    filters.hasAnyStock !== undefined || filters.isService !== undefined || filters.productType || filters.isActive !== undefined;

  if (warehousesLoading || categoriesLoading) {
    return <LoadingScreen />;
  }

  const { subtotal, taxAmount, total } = calculateTotals();

  return (
    <div className="p-6">

      {/* Raktár választás - csempék */}
      <div className="mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              <BuildingStorefrontIcon className="inline h-5 w-5 mr-2" />
              {t('sales.warehouse.title')}
            </label>
          </div>
          
          <div className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 xl:grid-cols-16 gap-1">
            {/* Bármelyik üzlet opció */}
            <div
              onClick={() => setSelectedWarehouse(null)}
              className={`p-1 border-2 rounded cursor-pointer transition-all duration-200 hover:shadow-sm ${
                selectedWarehouse === null
                  ? 'border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <div className="text-center">
                <Squares2X2Icon className={`h-4 w-4 mx-auto mb-0.5 ${
                  selectedWarehouse === null ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
                }`} />
                <div className={`font-medium text-xs truncate leading-none ${
                  selectedWarehouse === null ? 'text-primary-900 dark:text-primary-100' : 'text-gray-900 dark:text-gray-100'
                }`} title={t('sales.warehouse.allDescription')}>
                  {t('sales.warehouse.all')}
                </div>
                {selectedWarehouse === null && (
                  <CheckIcon className="h-2 w-2 text-primary-600 dark:text-primary-400 mx-auto mt-0.5" />
                )}
              </div>
            </div>
            
            {warehouses.filter(w => w.isActive && w.id).map(warehouse => {
              const WarehouseIcon = getWarehouseIcon(warehouse.salesChannelType);
              // Rövidebb név generálása
              const shortName = warehouse.name.length > 6 
                ? warehouse.name.substring(0, 5) + '.' 
                : warehouse.name;
              
              return (
                <div
                  key={warehouse.id}
                  onClick={() => setSelectedWarehouse(warehouse)}
                  className={`p-1 border-2 rounded cursor-pointer transition-all duration-200 hover:shadow-sm ${
                    selectedWarehouse?.id === warehouse.id
                      ? 'border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <div className="text-center">
                    <WarehouseIcon className={`h-4 w-4 mx-auto mb-0.5 ${
                      selectedWarehouse?.id === warehouse.id ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
                    }`} />
                    <div className={`font-medium text-xs truncate leading-none ${
                      selectedWarehouse?.id === warehouse.id ? 'text-primary-900 dark:text-primary-100' : 'text-gray-900 dark:text-gray-100'
                    }`} title={`${warehouse.name} - ${warehouse.salesChannelType || t('sales.warehouse.storage')}`}>
                      {shortName}
                    </div>
                    {selectedWarehouse?.id === warehouse.id && (
                      <CheckIcon className="h-2 w-2 text-primary-600 dark:text-primary-400 mx-auto mt-0.5" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Kategória választó */}
      <div className="mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              <TagIcon className="inline h-5 w-5 mr-2" />
              {t('sales.categories.title')}
            </label>
          </div>
          
          {/* Főkategóriák */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setFilters({...filters, categoryId: ''})}
                className={`text-sm px-3 py-1 rounded-full border transition-colors ${
                  filters.categoryId === '' 
                    ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-600 text-primary-700 dark:text-primary-300' 
                    : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
              >
                {t('sales.categories.all')}
              </button>
              {mainCategories.map(category => {
                // Megnézzük, hogy ez a főkategória van-e kiválasztva, vagy ez a főkategória tartalmazza a kiválasztott alkategóriát
                const isSelected = filters.categoryId === category.id || 
                  category.subcategories?.some(subCat => subCat.id === filters.categoryId);
                
                return (
                  <button
                    key={category.id}
                    onClick={() => setFilters({...filters, categoryId: category.id || ''})}
                    className={`text-sm px-3 py-1 rounded-full border transition-colors truncate max-w-40 ${
                      isSelected
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-600 text-primary-700 dark:text-primary-300'
                        : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`} 
                    title={category.name}
                  >
                    {category.name}
                    {category.subcategories && category.subcategories.length > 0 && (
                      <span className="ml-1 text-xs opacity-60">({category.subcategories.length})</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Alkategóriák - csak akkor jelenik meg, ha van kiválasztott főkategória */}
          {filters.categoryId && (() => {
            // Először megnézzük, hogy főkategória van-e kiválasztva
            let selectedMainCategory = mainCategories.find(cat => cat.id === filters.categoryId);
            
            // Ha nem található főkategória, akkor alkategória lehet kiválasztva - keressük meg a szülő főkategóriát
            if (!selectedMainCategory) {
              selectedMainCategory = mainCategories.find(mainCat => 
                mainCat.subcategories?.some(subCat => subCat.id === filters.categoryId)
              );
            }
            
            return selectedMainCategory?.subcategories && selectedMainCategory.subcategories.length > 0 ? (
              <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                <label className="block text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
                  {t('sales.categories.subcategories')} ({selectedMainCategory.name}):
                </label>
                <div className="flex flex-wrap gap-2">
                  {selectedMainCategory.subcategories.map(subCategory => (
                    <button
                      key={subCategory.id}
                      onClick={() => setFilters({...filters, categoryId: subCategory.id || ''})}
                      className={`text-xs px-2 py-1 rounded-full border transition-colors truncate max-w-32 ${
                        filters.categoryId === subCategory.id
                          ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-600 text-amber-700 dark:text-amber-300'
                          : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`} 
                      title={subCategory.name}
                    >
                      {subCategory.name}
                    </button>
                  ))}
                </div>
              </div>
            ) : null;
          })()}
        </div>
      </div>

      {/* Raktár választás után termékek megjelenítése */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Termékkeresés, szűrők és lista */}
        <div className="lg:col-span-2 space-y-4">
          {/* Keresés és szűrők - alapból nyitva */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            {/* Keresés */}
            <div className="relative mb-6">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder={t('sales.products.search')}
                value={filters.searchTerm}
                onChange={(e) => setFilters({...filters, searchTerm: e.target.value})}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 text-base bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>

            {/* Szűrők - mindig látszanak */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                  <FunnelIcon className="h-4 w-4 mr-2" />
                  Szűrők
                </h4>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                  >
                    {t('sales.products.clearFilters')}
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Készlet szűrő */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    <CubeIcon className="inline h-4 w-4 mr-1" />
                    Készlet ebben a raktárban
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setFilters({...filters, hasStock: undefined})}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        filters.hasStock === undefined
                          ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-600 text-primary-700 dark:text-primary-300'
                          : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      Minden termék
                    </button>
                    <button
                      onClick={() => setFilters({...filters, hasStock: true})}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        filters.hasStock === true
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-600 text-green-700 dark:text-green-300'
                          : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      Van készlet itt
                    </button>
                    <button
                      onClick={() => setFilters({...filters, hasStock: false})}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        filters.hasStock === false
                          ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-600 text-red-700 dark:text-red-300'
                          : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      Nincs készlet itt
                    </button>
                  </div>
                </div>

                {/* Termék típus */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    <Cog6ToothIcon className="inline h-4 w-4 mr-1" />
                    Típus
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setFilters({...filters, isService: undefined})}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        filters.isService === undefined
                          ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-600 text-primary-700 dark:text-primary-300'
                          : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      Termék és szolgáltatás
                    </button>
                    <button
                      onClick={() => setFilters({...filters, isService: false})}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        filters.isService === false
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-600 text-blue-700 dark:text-blue-300'
                          : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      Csak termékek
                    </button>
                    <button
                      onClick={() => setFilters({...filters, isService: true})}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        filters.isService === true
                          ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-600 text-purple-700 dark:text-purple-300'
                          : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      Csak szolgáltatások
                    </button>
                  </div>
                </div>

                {/* Aktív/Inaktív szűrő */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    <CheckIcon className="inline h-4 w-4 mr-1" />
                    Állapot
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setFilters({...filters, isActive: undefined})}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        filters.isActive === undefined
                          ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-600 text-primary-700 dark:text-primary-300'
                          : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      Aktív és inaktív
                    </button>
                    <button
                      onClick={() => setFilters({...filters, isActive: true})}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        filters.isActive === true
                          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-600 text-green-700 dark:text-green-300'
                          : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      Csak aktív termékek
                    </button>
                    <button
                      onClick={() => setFilters({...filters, isActive: false})}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        filters.isActive === false
                          ? 'bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-700 dark:text-gray-300'
                          : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      Csak inaktív termékek
                    </button>
                  </div>
                </div>
              </div>

              {/* Általános készlet szűrő */}
              <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Általános készlet állapot:
                </label>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={() => setFilters({...filters, hasAnyStock: undefined})}
                    className={`text-sm px-4 py-2 rounded-full border transition-colors ${
                      filters.hasAnyStock === undefined
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-600 text-primary-700 dark:text-primary-300'
                        : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    Minden termék
                  </button>
                  <button
                    onClick={() => setFilters({...filters, hasAnyStock: true})}
                    className={`text-sm px-4 py-2 rounded-full border transition-colors ${
                      filters.hasAnyStock === true
                        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-600 text-green-700 dark:text-green-300'
                        : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    Van készlet bárhol
                  </button>
                  <button
                    onClick={() => setFilters({...filters, hasAnyStock: false})}
                    className={`text-sm px-4 py-2 rounded-full border transition-colors ${
                      filters.hasAnyStock === false
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-600 text-red-700 dark:text-red-300'
                        : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                    }`}
                  >
                    Nincs készlet sehol
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Termékek listája */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Termékek</h3>
              <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                <span>{pagination.totalItems} termék található</span>
                <span>•</span>
                <span>
                  {pagination.totalItems > 0 && (
                    <>
                      {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, pagination.totalItems)} 
                      {' '}/ {pagination.totalItems}
                    </>
                  )}
                </span>
                <span>•</span>
                <span>{selectedWarehouse ? selectedWarehouse.name : 'Összes raktár'}</span>
              </div>
            </div>

            {/* Oldalméret választó */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <label className="text-sm text-gray-600 dark:text-gray-400">Termékek oldalanként:</label>
                <div className="flex gap-1">
                  {[25, 50, 100, 200].map(size => (
                    <button
                      key={size}
                      onClick={() => {
                        setPageSize(size);
                        setCurrentPage(1);
                      }}
                      className={`px-3 py-1 text-sm rounded border transition-colors ${
                        pageSize === size
                          ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-600 text-primary-700 dark:text-primary-300'
                          : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Quick pagination */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={goToPreviousPage}
                    disabled={!pagination.hasPreviousPage}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {currentPage} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={goToNextPage}
                    disabled={!pagination.hasNextPage}
                    className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            
            {productsLoading ? (
              <LoadingScreen />
            ) : products.length === 0 ? (
              <div className="text-center py-12">
                <CubeIcon className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {filters.searchTerm || hasActiveFilters ? 'Nincs találat' : 'Nincsenek termékek'}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  {filters.searchTerm || hasActiveFilters 
                    ? 'Próbáld más keresési feltétellel vagy módosítsd a szűrőket' 
                    : 'Termékek betöltése vagy hozzáadása szükséges'
                  }
                </p>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 font-medium"
                  >
                    Szűrők törlése
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {products.map(product => {
                    const stockInfo = getStockInfo(product.id);
                    return (
                      <div
                        key={product.id}
                        onClick={() => addToCart(product)}
                        className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                          stockInfo.currentWarehouseStock > 0 
                            ? 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600' 
                            : stockInfo.stockSources.length > 0
                              ? 'border-orange-200 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-800 hover:border-orange-300 dark:hover:border-orange-600'
                              : 'border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 cursor-not-allowed opacity-75'
                        } ${!product.isActive ? 'opacity-60' : ''}`}
                      >
                        {/* Termék kép és alapadatok */}
                        <div className="flex gap-3 mb-3">
                          {/* Termék kép */}
                          <div className="flex-shrink-0">
                            {product.thumbnailUrl ? (
                              <img
                                src={product.thumbnailUrl}
                                alt={product.name}
                                className="w-16 h-16 object-cover rounded-md border border-gray-200 dark:border-gray-700"
                                onError={(e) => {
                                  // Ha a kép betöltése sikertelen, placeholder-t mutatunk
                                  const target = e.currentTarget;
                                  const placeholder = target.nextElementSibling as HTMLElement;
                                  target.style.display = 'none';
                                  if (placeholder) {
                                    placeholder.style.display = 'flex';
                                  }
                                }}
                              />
                            ) : null}
                            {/* Placeholder ha nincs kép vagy nem töltődik be */}
                            <div 
                              className={`w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-700 flex items-center justify-center ${
                                product.thumbnailUrl ? 'hidden' : 'flex'
                              }`}
                            >
                              <CubeIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                            </div>
                          </div>

                          {/* Termék információk */}
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start mb-1">
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold text-gray-900 dark:text-gray-100 truncate text-base flex items-center" title={product.name}>
                                  {product.name}
                                  {!product.isActive && (
                                    <span className="ml-2 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-xs px-2 py-1 rounded-full flex-shrink-0">
                                      Inaktív
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400 font-mono">{product.code}</div>
                              </div>
                              {product.isService && (
                                <span className="ml-2 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 text-xs px-2 py-1 rounded-full flex-shrink-0">
                                  Szolgáltatás
                                </span>
                              )}
                            </div>
                            
                            <div className="text-lg font-bold text-primary-600 dark:text-primary-400 mb-2">
                              {new Intl.NumberFormat('hu-HU').format(Math.round(product.netPrice))} Ft
                              {product.unit && product.unit !== 'db' && (
                                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">/{product.unit}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Kategória és Készlet info egy sorban */}
                        <div className="flex items-center justify-between text-sm">
                          {/* Kategória */}
                          <div className="flex items-center text-gray-600 dark:text-gray-400">
                            {product.category ? (
                              <>
                                <TagIcon className="h-4 w-4 mr-1" />
                                <span className="truncate max-w-32" title={product.category.name}>
                                  {product.category.name}
                                </span>
                              </>
                            ) : (
                              <>
                                <TagIcon className="h-4 w-4 mr-1 text-gray-300 dark:text-gray-500" />
                                <span className="text-gray-400 dark:text-gray-500">Nincs kategória</span>
                              </>
                            )}
                          </div>
                          
                          {/* Készlet info */}
                          <div className="flex items-center">
                            <CubeIcon className="h-4 w-4 mr-1 text-gray-600 dark:text-gray-400" />
                            {selectedWarehouse === null ? (
                              // Összes raktár mód
                              stockInfo.currentWarehouseStock > 0 ? (
                                <span className="text-green-600 dark:text-green-400 font-medium">
                                  {stockInfo.currentWarehouseStock} db
                                </span>
                              ) : (
                                <span className="text-red-600 dark:text-red-400 font-medium">Nincs</span>
                              )
                            ) : (
                              // Kiválasztott raktár mód
                              <>
                                {stockInfo.currentWarehouseStock > 0 ? (
                                  <span className="text-green-600 dark:text-green-400 font-medium">
                                    {stockInfo.currentWarehouseStock} db
                                  </span>
                                ) : stockInfo.stockSources.length > 0 ? (
                                  <span className="text-orange-600 dark:text-orange-400 font-medium flex items-center">
                                    Átkérhető
                                    <ArrowRightIcon className="h-3 w-3 ml-1" />
                                  </span>
                                ) : (
                                  <span className="text-red-600 dark:text-red-400 font-medium">Nincs</span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Paginációs vezérlők */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {pagination.totalItems} termékből {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, pagination.totalItems)} megjelenítve
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {/* Első oldal */}
                      {currentPage > 3 && (
                        <>
                          <button
                            onClick={() => goToPage(1)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                          >
                            1
                          </button>
                          {currentPage > 4 && <span className="text-gray-400 dark:text-gray-500">...</span>}
                        </>
                      )}
                      
                      {/* Előző oldal */}
                      <button
                        onClick={goToPreviousPage}
                        disabled={!pagination.hasPreviousPage}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      
                      {/* Oldalszámok */}
                      {getPageNumbers().map(pageNum => (
                        <button
                          key={pageNum}
                          onClick={() => goToPage(pageNum)}
                          className={`px-3 py-2 border rounded text-sm ${
                            pageNum === currentPage
                              ? 'border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400'
                              : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {pageNum}
                        </button>
                      ))}
                      
                      {/* Következő oldal */}
                      <button
                        onClick={goToNextPage}
                        disabled={!pagination.hasNextPage}
                        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                      
                      {/* Utolsó oldal */}
                      {currentPage < pagination.totalPages - 2 && (
                        <>
                          {currentPage < pagination.totalPages - 3 && <span className="text-gray-400 dark:text-gray-500">...</span>}
                          <button
                            onClick={() => goToPage(pagination.totalPages)}
                            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                          >
                            {pagination.totalPages}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Jobb oldali panel */}
        <div className="space-y-4">
          {selectedWarehouse ? (
            <>
              {/* Kosár */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-md font-medium text-gray-900 dark:text-gray-100">Kosár</h3>
                  <ShoppingCartIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                
                {cart.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">Üres kosár</p>
                ) : (
                  <div className="space-y-2">
                    {cart.map(item => (
                      <div key={item.productId} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-700 rounded">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {item.productName}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {new Intl.NumberFormat('hu-HU').format(Math.round(item.unitPrice))} Ft/db
                          </div>
                          <div className="text-xs text-green-600 dark:text-green-400">
                            {item.availableInCurrentWarehouse} db készleten
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-600 dark:text-gray-400"
                          >
                            <MinusIcon className="h-4 w-4" />
                          </button>
                          <span className="text-sm font-medium w-8 text-center text-gray-900 dark:text-gray-100">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded text-gray-600 dark:text-gray-400"
                          >
                            <PlusIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => removeFromCart(item.productId)}
                            className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 rounded"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Összesítő */}
              {cart.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">Összesítő</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-gray-700 dark:text-gray-300">
                      <span>Nettó összeg:</span>
                      <span>{new Intl.NumberFormat('hu-HU').format(Math.round(subtotal))} Ft</span>
                    </div>
                    <div className="flex justify-between text-gray-700 dark:text-gray-300">
                      <span>ÁFA:</span>
                      <span>{new Intl.NumberFormat('hu-HU').format(Math.round(taxAmount))} Ft</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t border-gray-200 dark:border-gray-600 pt-2 text-gray-900 dark:text-gray-100">
                      <span>Fizetendő:</span>
                      <span>{new Intl.NumberFormat('hu-HU').format(Math.round(total))} Ft</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Fizetési mód */}
              {cart.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">Fizetési mód</h3>
                  <div className="space-y-2">
                    {[
                      { value: 'CASH', label: 'Készpénz', icon: '💵' },
                      { value: 'CARD', label: 'Bankkártya', icon: '💳' },
                      { value: 'TRANSFER', label: 'Átutalás', icon: '🏦' }
                    ].map(method => (
                      <label key={method.value} className="flex items-center text-gray-700 dark:text-gray-300">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={method.value}
                          checked={paymentMethod === method.value}
                          onChange={(e) => setPaymentMethod(e.target.value as any)}
                          className="mr-3"
                        />
                        <span className="mr-2">{method.icon}</span>
                        <span>{method.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Vásárló adatok */}
              {cart.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-md font-medium text-gray-900 dark:text-gray-100">Vásárló (opcionális)</h3>
                    <button
                      onClick={() => setShowCustomerForm(!showCustomerForm)}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300"
                    >
                      {showCustomerForm ? 'Elrejt' : 'Hozzáad'}
                    </button>
                  </div>
                  
                  {showCustomerForm && (
                    <div className="space-y-3">
                      <input
                        type="text"
                        placeholder="Név"
                        value={customer.name || ''}
                        onChange={(e) => setCustomer({...customer, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={customer.email || ''}
                        onChange={(e) => setCustomer({...customer, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
                      />
                      <input
                        type="tel"
                        placeholder="Telefon"
                        value={customer.phone || ''}
                        onChange={(e) => setCustomer({...customer, phone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Értékesítés gomb */}
              {cart.length > 0 && (
                <button
                  onClick={handleSale}
                  className="w-full bg-primary-600 dark:bg-primary-700 text-white py-3 px-4 rounded-md hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors flex items-center justify-center font-medium"
                >
                  <CheckIcon className="h-5 w-5 mr-2" />
                  Értékesítés és számla kiállítása
                </button>
              )}
            </>
          ) : (
            /* Info panel böngészés módban */
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <div className="text-center">
                <BuildingStorefrontIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Böngészés mód</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Most az összes raktár készleteit látod. 
                  <br />
                  Értékesítéshez válassz ki egy konkrét raktárat a fenti listából.
                </p>
                <div className="space-y-2 text-sm text-gray-500 dark:text-gray-400">
                  <div className="flex items-center justify-center">
                    <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                    <span>Zöld = Van készlet a termékből</span>
                  </div>
                  <div className="flex items-center justify-center">
                    <span className="inline-block w-3 h-3 bg-orange-500 rounded-full mr-2"></span>
                    <span>Narancssárga = Átkérhető készlet</span>
                  </div>
                  <div className="flex items-center justify-center">
                    <span className="inline-block w-3 h-3 bg-red-500 rounded-full mr-2"></span>
                    <span>Piros = Nincs készlet</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Készlet Modal - Raktárközi átkérés */}
      {showStockModal && selectedProduct && (
        <div className="fixed inset-0 bg-gray-600 dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Raktárközi átkérés
              </h3>
              <button
                onClick={() => {
                  setShowStockModal(false);
                  setSelectedProduct(null);
                }}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              <h4 className="font-medium text-gray-900 dark:text-gray-100">{selectedProduct.name}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                A termék nem áll rendelkezésre a(z) <strong className="text-gray-900 dark:text-gray-100">{selectedWarehouse?.name}</strong> raktárban.
              </p>
            </div>

            <div className="space-y-3">
              <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Elérhető készletek:</h5>
              {getStockInfo(selectedProduct.id).stockSources.map(source => (
                <div key={source.warehouseId} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                  <div>
                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">{source.warehouseName}</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">{source.availableQuantity} db elérhető</div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      min="1"
                      max={source.availableQuantity}
                      defaultValue="1"
                      className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                      id={`quantity-${source.warehouseId}`}
                    />
                    <button
                      onClick={() => {
                        const input = document.getElementById(`quantity-${source.warehouseId}`) as HTMLInputElement;
                        const quantity = parseInt(input.value) || 1;
                        requestStockTransfer(source.warehouseId, quantity);
                      }}
                      className="px-3 py-1 bg-primary-600 dark:bg-primary-700 text-white rounded text-sm hover:bg-primary-700 dark:hover:bg-primary-600"
                    >
                      Átkérés
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => {
                  setShowStockModal(false);
                  setSelectedProduct(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Bezárás
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        confirmText={notification.confirmText}
        cancelText={notification.cancelText}
        onConfirm={notification.onConfirm}
        showCancel={notification.showCancel}
      />
    </div>
  );
};

export default SalesChannelList; 